import { BadgeCheck, LogOut, Smartphone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError, endpoints } from '../../services/api';
import { isLicensed, startMicrosoft, useSession } from '../../store/session';
import { PairWithPhone } from '../PairWithPhone';
import { PayOnPhone } from '../PayOnPhone';
import { buttonClass, ghostButtonClass, inputClass, Section } from './controls';

export async function startCheckout(): Promise<string | null> {
  try {
    const { url } = await endpoints.checkout();
    window.location.assign(url);
    return null;
  } catch (e) {
    return e instanceof ApiError && e.code === 'not_configured'
      ? 'Payments are not switched on yet.'
      : e instanceof Error
        ? e.message
        : 'Checkout failed';
  }
}

export function AccountTab() {
  const { config, user, signOut, microsoft, refreshMicrosoft } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pairing, setPairing] = useState(false);

  const accounts = config?.features.accounts ?? false;
  const billing = config?.features.billing ?? false;

  const buy = async () => {
    setBusy(true);
    setError(await startCheckout());
    setBusy(false);
  };

  if (!accounts) {
    return (
      <Section title="Account">
        <p className="muted text-sm">
          Accounts aren’t switched on for this server yet. Everything works on this screen without one; signing in will add
          syncing between screens, cloud photo libraries and ad-free licences.
        </p>
      </Section>
    );
  }

  if (user) {
    const licensed = isLicensed(user);
    return (
      <>
        <Section title="Signed in">
          <p className="text-sm">{user.email}</p>
          <p className="muted text-xs">Settings and lists sync to every screen signed in to this account.</p>
          {user.admin && (
            <a href="/admin" className="text-sm text-[var(--accent)] hover:underline">
              Manage house ads →
            </a>
          )}
          <button className={ghostButtonClass} onClick={() => void signOut()}>
            <LogOut className="h-4 w-4" aria-hidden /> Sign out
          </button>
        </Section>
        {config?.features.microsoft && (
          <Section title="Microsoft account" description="OneDrive photos, Outlook calendars and To Do lists.">
            {microsoft?.connected ? (
              <>
                <p className="text-sm">Connected{microsoft.email ? ` as ${microsoft.email}` : ''}.</p>
                {microsoft.work &&
                  (microsoft.workAccess ? (
                    <p className="muted text-xs">
                      Work or school account: SharePoint libraries, shared folders, shared calendars and Planner are available.
                    </p>
                  ) : (
                    <div className="space-y-2 rounded-lg border border-[var(--border)] p-3">
                      <p className="text-sm">
                        This is a work or school account. Allow access to also use photos from SharePoint libraries and folders
                        shared with you, and calendars other people share with you. Planner plans already appear with your lists.
                      </p>
                      <button className={buttonClass} onClick={() => startMicrosoft('link', undefined, true)}>
                        <MicrosoftLogo /> Allow SharePoint and shared items
                      </button>
                    </div>
                  ))}
                <button
                  className={ghostButtonClass}
                  onClick={async () => {
                    await endpoints.microsoftDisconnect().catch(() => undefined);
                    await refreshMicrosoft();
                  }}
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button className={buttonClass} onClick={() => startMicrosoft('link')}>
                <MicrosoftLogo /> Connect Microsoft account
              </button>
            )}
          </Section>
        )}
        <Section title="Licence">
          {licensed ? (
            <p className="flex items-center gap-2 text-sm">
              <BadgeCheck className="h-5 w-5 text-[var(--accent)]" aria-hidden /> Lifetime licence – ad-free on every screen.
            </p>
          ) : (
            <>
              <p className="text-sm">
                KCL Canvas is free, with an occasional short ad. A one-off <strong>£10 lifetime licence</strong> (plus local tax, shown at checkout) removes ads on every
                screen you sign in to.
              </p>
              {billing && <PayOnPhone />}
              <button className={billing ? ghostButtonClass : buttonClass} onClick={() => void buy()} disabled={busy || !billing}>
                {billing ? 'Or pay on this device – £10 + tax' : 'Licences coming soon'}
              </button>
            </>
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}
        </Section>
      </>
    );
  }

  return (
    <>
      <Section title="Sign in with your phone" description="Easiest on a TV: scan a code and approve on your phone.">
        {pairing ? (
          <PairWithPhone onCancel={() => setPairing(false)} />
        ) : (
          <button className={buttonClass} onClick={() => setPairing(true)}>
            <Smartphone className="h-4 w-4" aria-hidden /> Show sign-in code
          </button>
        )}
      </Section>
      <SignInForm />
    </>
  );
}

/** Email/password (+ Microsoft) sign-in. `returnTo` is where a Microsoft sign-in lands afterwards. */
export function SignInForm({ returnTo }: { returnTo?: string }) {
  const { config, setUser } = useSession();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = mode === 'login' ? await endpoints.login(email, password) : await endpoints.register(email, password);
      setUser(res.user);
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title={mode === 'login' ? 'Sign in' : 'Create an account'}
      description={
        config?.features.microsoft
          ? 'Google, Apple and Amazon sign-in are coming soon.'
          : 'Sign in with Microsoft, Google, Apple or Amazon is coming soon.'
      }
    >
      {config?.features.microsoft && (
        <>
          <button
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border)] bg-white px-3 py-2.5 text-sm font-medium text-[#1b1b1b] hover:bg-white/90"
            onClick={() => startMicrosoft('signin', returnTo)}
          >
            <MicrosoftLogo /> Continue with Microsoft
          </button>
          <div className="muted flex items-center gap-3 text-xs">
            <span className="h-px flex-1 bg-[var(--border)]" /> or with email <span className="h-px flex-1 bg-[var(--border)]" />
          </div>
        </>
      )}
      <form onSubmit={submit} className="space-y-3">
        <input
          className={inputClass}
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-label="Email"
        />
        <input
          className={inputClass}
          type="password"
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          placeholder={mode === 'login' ? 'Password' : 'Password (at least 10 characters)'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={mode === 'register' ? 10 : undefined}
          required
          aria-label="Password"
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button className={`${buttonClass} w-full`} disabled={busy} type="submit">
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </button>
      </form>
      <button
        className="muted text-sm underline-offset-2 hover:underline"
        onClick={() => {
          setMode(mode === 'login' ? 'register' : 'login');
          setError(null);
        }}
      >
        {mode === 'login' ? 'New here? Create an account' : 'Already have an account? Sign in'}
      </button>
      <p className="muted text-xs">
        By continuing you agree to the{' '}
        <a href="/terms" className="underline">
          Terms
        </a>{' '}
        and{' '}
        <a href="/privacy" className="underline">
          Privacy Policy
        </a>
        .
      </p>
    </Section>
  );
}

/** The four-square Microsoft logo, as required by Microsoft's sign-in branding guidelines. */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
